/**
 * DiningSettings - Categories and Daily Specials management
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Settings, Plus, Edit2, Trash2, Star, Coffee,
  UtensilsCrossed, Salad, Sandwich, Cake, Wine, AlertTriangle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = [
  { value: '☕', label: 'Coffee' },
  { value: '🍳', label: 'Breakfast' },
  { value: '🥗', label: 'Salads' },
  { value: '🍔', label: 'Burgers' },
  { value: '🍕', label: 'Pizza' },
  { value: '🍝', label: 'Pasta' },
  { value: '🍖', label: 'Meat' },
  { value: '🐟', label: 'Seafood' },
  { value: '🥤', label: 'Drinks' },
  { value: '🍰', label: 'Desserts' },
  { value: '🍺', label: 'Beer' },
  { value: '🍷', label: 'Wine' },
  { value: '🥪', label: 'Sandwiches' },
  { value: '🌮', label: 'Tacos' },
  { value: '🍜', label: 'Noodles' },
  { value: '🍱', label: 'Asian' }
];

const DiningSettings = () => {
  const queryClient = useQueryClient();
  const [showAddCategoryDialog, setShowAddCategoryDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddSpecialDialog, setShowAddSpecialDialog] = useState(false);

  const token = getToken();
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  // Fetch categories
  const { data: categories = [], isLoading: categoriesLoading } = useQuery({
    queryKey: ['dining-categories'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch menu items for specials
  const { data: menuItems = [] } = useQuery({
    queryKey: ['dining-menu-all'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/menu`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Fetch today's specials
  const { data: specials = [], isLoading: specialsLoading } = useQuery({
    queryKey: ['today-specials'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/dining/specials`, { headers });
      if (!res.ok) return [];
      return res.json();
    }
  });

  // Create category
  const createCategoryMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create category');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-categories']);
      queryClient.invalidateQueries(['dining-stats']);
      setShowAddCategoryDialog(false);
      toast.success('Category created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Update category
  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories/${id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update category');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-categories']);
      setEditingCategory(null);
      toast.success('Category updated');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete category
  const deleteCategoryMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/categories/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete category');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['dining-categories']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success('Category deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  // Create special
  const createSpecialMutation = useMutation({
    mutationFn: async (data) => {
      const res = await fetch(`${API_URL}/api/admin/dining/specials`, {
        method: 'POST',
        headers,
        body: JSON.stringify(data)
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to create special');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['today-specials']);
      queryClient.invalidateQueries(['dining-stats']);
      setShowAddSpecialDialog(false);
      toast.success('Special created');
    },
    onError: (error) => toast.error(error.message)
  });

  // Delete special
  const deleteSpecialMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/api/admin/dining/specials/${id}`, {
        method: 'DELETE',
        headers
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to delete special');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['today-specials']);
      queryClient.invalidateQueries(['dining-stats']);
      toast.success('Special deleted');
    },
    onError: (error) => toast.error(error.message)
  });

  const CategoryForm = ({ category, onSubmit, onCancel, isLoading }) => {
    const [formData, setFormData] = useState({
      name: category?.name || '',
      description: category?.description || '',
      icon: category?.icon || '🍽️',
      display_order: category?.display_order || 0
    });

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Category Name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="e.g., Breakfast"
            data-testid="input-category-name"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Description</label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Brief description"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Icon</label>
            <Select
              value={formData.icon}
              onValueChange={(value) => setFormData({ ...formData, icon: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_ICONS.map((icon) => (
                  <SelectItem key={icon.value} value={icon.value}>
                    {icon.value} {icon.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium">Display Order</label>
            <Input
              type="number"
              min="0"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading} data-testid="submit-category-btn">
            {isLoading ? 'Saving...' : category ? 'Update Category' : 'Create Category'}
          </Button>
        </div>
      </form>
    );
  };

  const SpecialForm = ({ onSubmit, onCancel, isLoading }) => {
    const today = new Date().toISOString().split('T')[0];
    const [formData, setFormData] = useState({
      menu_item_id: '',
      special_price: 0,
      date: today,
      description: '',
      quantity_limit: null
    });

    const selectedItem = menuItems.find(i => i.id === formData.menu_item_id);

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(formData);
        }}
        className="space-y-4"
      >
        <div>
          <label className="text-sm font-medium">Menu Item *</label>
          <Select
            value={formData.menu_item_id}
            onValueChange={(value) => {
              const item = menuItems.find(i => i.id === value);
              setFormData({ 
                ...formData, 
                menu_item_id: value,
                special_price: item?.price ? item.price * 0.8 : 0 // Default 20% off
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select item" />
            </SelectTrigger>
            <SelectContent>
              {menuItems.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name} (${item.price?.toFixed(2)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedItem && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm">Original price: <strong>${selectedItem.price?.toFixed(2)}</strong></p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Special Price ($) *</label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={formData.special_price}
              onChange={(e) => setFormData({ ...formData, special_price: parseFloat(e.target.value) || 0 })}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Date</label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              min={today}
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium">Description (optional)</label>
          <Input
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="e.g., Happy Hour Special"
          />
        </div>

        <div>
          <label className="text-sm font-medium">Quantity Limit (optional)</label>
          <Input
            type="number"
            min="1"
            value={formData.quantity_limit || ''}
            onChange={(e) => setFormData({ ...formData, quantity_limit: parseInt(e.target.value) || null })}
            placeholder="Leave empty for unlimited"
          />
        </div>

        {selectedItem && formData.special_price > 0 && (
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-sm">
              Discount: <strong className="text-green-600">
                {((1 - formData.special_price / selectedItem.price) * 100).toFixed(0)}% off
              </strong>
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
          <Button type="submit" disabled={isLoading || !formData.menu_item_id} data-testid="submit-special-btn">
            {isLoading ? 'Creating...' : 'Create Special'}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className="space-y-6" data-testid="dining-settings">
      <Tabs defaultValue="categories">
        <TabsList className="mb-4">
          <TabsTrigger value="categories">
            <Coffee className="h-4 w-4 mr-2" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="specials">
            <Star className="h-4 w-4 mr-2" />
            Daily Specials
          </TabsTrigger>
        </TabsList>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Menu Categories</h2>
              <p className="text-sm text-gray-500">Organize your menu into categories</p>
            </div>
            <Button onClick={() => setShowAddCategoryDialog(true)} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Category
            </Button>
          </div>

          {categoriesLoading ? (
            <div className="text-center py-8 text-gray-500">Loading categories...</div>
          ) : categories.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Coffee className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No categories yet</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Create categories to organize your menu
                </p>
                <Button onClick={() => setShowAddCategoryDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Category
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((category) => (
                <Card key={category.id} data-testid={`category-card-${category.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <span className="text-3xl">{category.icon}</span>
                        <div>
                          <h3 className="font-medium">{category.name}</h3>
                          <p className="text-sm text-gray-500">{category.item_count || 0} items</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => setEditingCategory(category)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => {
                            if (confirm('Delete this category?')) {
                              deleteCategoryMutation.mutate(category.id);
                            }
                          }}
                          disabled={category.item_count > 0}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    {category.description && (
                      <p className="text-sm text-gray-500">{category.description}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Specials Tab */}
        <TabsContent value="specials" className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Daily Specials</h2>
              <p className="text-sm text-gray-500">Set special prices for today</p>
            </div>
            <Button 
              onClick={() => setShowAddSpecialDialog(true)} 
              disabled={menuItems.length === 0}
              data-testid="add-special-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Special
            </Button>
          </div>

          {specialsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading specials...</div>
          ) : specials.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Star className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-medium mb-2">No specials today</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Add daily specials to attract more customers
                </p>
                <Button 
                  onClick={() => setShowAddSpecialDialog(true)}
                  disabled={menuItems.length === 0}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Today's Special
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {specials.map((special) => (
                <Card key={special.id} className="bg-yellow-50 border-yellow-200" data-testid={`special-card-${special.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge variant="warning" className="mb-2">
                          <Star className="h-3 w-3 mr-1" />
                          Today's Special
                        </Badge>
                        <h3 className="font-medium">{special.menu_item_name}</h3>
                        {special.description && (
                          <p className="text-sm text-gray-500">{special.description}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          if (confirm('Remove this special?')) {
                            deleteSpecialMutation.mutate(special.id);
                          }
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <span className="text-sm line-through text-gray-400">
                        ${special.original_price?.toFixed(2)}
                      </span>
                      <span className="text-xl font-bold text-green-600">
                        ${special.special_price?.toFixed(2)}
                      </span>
                      <Badge variant="outline" className="text-green-600">
                        {((1 - special.special_price / special.original_price) * 100).toFixed(0)}% off
                      </Badge>
                    </div>
                    {special.quantity_limit && (
                      <p className="text-xs text-gray-500 mt-2">
                        Limited: {special.quantity_sold || 0}/{special.quantity_limit} sold
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Category Dialog */}
      <Dialog open={showAddCategoryDialog} onOpenChange={setShowAddCategoryDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Category</DialogTitle>
          </DialogHeader>
          <CategoryForm
            onSubmit={(data) => createCategoryMutation.mutate(data)}
            onCancel={() => setShowAddCategoryDialog(false)}
            isLoading={createCategoryMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Category Dialog */}
      <Dialog open={!!editingCategory} onOpenChange={(open) => !open && setEditingCategory(null)}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Category</DialogTitle>
          </DialogHeader>
          {editingCategory && (
            <CategoryForm
              category={editingCategory}
              onSubmit={(data) => updateCategoryMutation.mutate({ id: editingCategory.id, data })}
              onCancel={() => setEditingCategory(null)}
              isLoading={updateCategoryMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add Special Dialog */}
      <Dialog open={showAddSpecialDialog} onOpenChange={setShowAddSpecialDialog}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Daily Special</DialogTitle>
          </DialogHeader>
          <SpecialForm
            onSubmit={(data) => createSpecialMutation.mutate(data)}
            onCancel={() => setShowAddSpecialDialog(false)}
            isLoading={createSpecialMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiningSettings;
